import { AppstoreAddOutlined, EyeOutlined } from '@ant-design/icons';
import React, { useMemo } from 'react';

import { useBedCategories } from 'app/hooks/data/rooms/useBedCategories';
import { BABY_COT_BED_CATEGORY_ID } from 'app/typings';
import { maxRoomCapacity } from 'app/utils/room';
import { pluralize } from 'app/utils/strings';

import type { Capacity, Room } from '../../../ExtranetPackages.types';
import { GenericTable } from '../../GenericTable/GenericTable';
import {
  ImageTextCell,
  TagCell,
  TextCell,
} from '../../GenericTable/TableCellRenderers';
import { useTableColumns } from '../../GenericTable/useTableColumns';

type RoomsTableProps = {
  dataSource?: Room[];
  loading?: boolean;
  onViewRoom?: (room: Room) => void;
};

const formatCapacity = (capacity: Capacity): React.ReactNode => {
  if (!capacity) {
    return undefined;
  }

  const guests = capacity.guest
    ? pluralize(capacity.guest, 'guest', 'guests')
    : null;
  const babies = capacity.baby
    ? pluralize(capacity.baby, 'baby', 'babies')
    : null;

  if (guests && babies) {
    return (
      <>
        <div>{guests}</div>
        <div>+ {babies}</div>
      </>
    );
  }

  return guests ?? babies ?? undefined;
};

export const RoomsTable: React.FC<RoomsTableProps> = ({
  dataSource = [],
  loading,
  onViewRoom,
}) => {
  const { data: bedCategories } = useBedCategories();

  const columnsConfig = useMemo(
    () => [
      {
        key: 'name',
        title: 'Room name',
        render: (_: unknown, record: Room) => (
          <ImageTextCell
            images={record.images}
            title={record.name}
            subtitle={record.categoryName}
          />
        ),
      },
      {
        key: 'capacity',
        title: 'Capacity',
        width: '140px',

        render: (_: unknown, record: Room) => {
          const guests = maxRoomCapacity(record.beds, bedCategories ?? []);
          const baby = record.beds?.some(
            (b) => b.categoryId === BABY_COT_BED_CATEGORY_ID
          )
            ? 1
            : 0;

          return <TextCell value={formatCapacity({ guest: guests, baby })} />;
        },
      },
      {
        key: 'beds',
        title: 'Beds',
        width: '325px',
        render: (value: Room['beds'], record: Room) => (
          <TagCell
            values={value?.map((bed) => bed.name)}
            icon={<EyeOutlined />}
            onIconClick={() => onViewRoom?.(record)}
          />
        ),
      },
    ],
    [bedCategories, onViewRoom]
  );

  const columns = useTableColumns(columnsConfig);

  return (
    <GenericTable
      title="Rooms"
      columns={columns}
      dataSource={dataSource}
      loading={loading}
      emptyState={{
        title: 'No rooms',
        description:
          'This package has no linked rooms. Contact your Account Manager for more information.',
        icon: <AppstoreAddOutlined />,
      }}
    />
  );
};
